import type { RefOrGetter } from "ls/common/types";
import { useQuery } from "@tanstack/vue-query";
import { useIsOnPrem } from "ls/state/useIsOnPrem";
import { computed, toValue } from "vue";
import { createTwoWayMappers } from "@/common/lib/object";
import { CloudSubscriptionTerm } from "@/generated/models";
import { useAuth } from "../user/useAuth";
import { queries } from "./queries";
import { useOrganizationAdminInfo } from "./useOrganizationAdminInfo";

export function useSubInstanceLicensingInfo(organizationId: RefOrGetter<string>) {
  const subInstanceAdminInfo = useOrganizationAdminInfo(organizationId);
  const { user } = useAuth();

  const isCurrentUserSubInstanceOwner = computed(() => {
    if (!subInstanceAdminInfo.value) return false;

    if (!user.value) return false;

    return subInstanceAdminInfo.value.OwnerEmail === user.value.userEmail;
  });

  const ownerEmail = computed(() => {
    if (!subInstanceAdminInfo.value) return "";

    return subInstanceAdminInfo.value.OwnerEmail;
  });

  return {
    isCurrentUserSubInstanceOwner,
    ownerEmail,
  };
}

export function useSubInstanceSubscriptionsInfo(organizationId: RefOrGetter<string>) {
  const isOnPrem = useIsOnPrem();
  const options = computed(() => {
    return {
      ...queries.subscriptionsInfo(toValue(organizationId)),
      enabled: !isOnPrem.value,
    };
  });
  const query = useQuery(options);
  return query.data;
}

export const cloudSubscriptionTermMap = {
  [CloudSubscriptionTerm.Annual]: "Annual",
  [CloudSubscriptionTerm.Manual]: "Manual",
  [CloudSubscriptionTerm.Monthly]: "Monthly",
  [CloudSubscriptionTerm.Perpetual]: "Perpetual",
  [CloudSubscriptionTerm.Year2]: "2 year",
  [CloudSubscriptionTerm.Year3]: "3 year",
} as const;

export const [toCloudSubscriptionTerm, fromCloudSubscriptionTerm] = createTwoWayMappers(cloudSubscriptionTermMap);
